import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest, ApiError } from "src/services/apiClient";
import { notifyRecipientDataStale } from "src/services/recipientDataRefresh";
import type { Recipient } from "src/types";

const POLL_MS = 15_000;
// Consent and patient data can change from another team member's login — keep an open detail
// page current for everyone, like useLiveRecipientsList, not just while a questionnaire is pending.
const IDLE_POLL_MS = 60_000;

function snapshotRecipient(r: Recipient): Recipient {
    return {
        ...r,
        patients: r.patients ? r.patients.map((p) => ({ ...p })) : [],
        tel: r.tel ? [...r.tel] : [],
        invoices: r.invoices ? [...r.invoices] : r.invoices,
        _liveAt: Date.now(),
    } as Recipient;
}

/**
 * Recipient detail with aggressive live reload while a questionnaire is pending.
 * Bypasses useApi cache so portal submissions show up without navigating away.
 */
export function useLiveRecipientDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [recipient, setRecipient] = useState<Recipient | null>(null);
    const [loading, setLoading] = useState(!!id);
    const [error, setError] = useState<string | null>(null);
    const prevStatusRef = useRef<string | undefined>();
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const onSubmittedRef = useRef<(() => void) | null>(null);

    const stopPoll = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    const load = useCallback(async (): Promise<Recipient | null> => {
        if (!id) return null;
        try {
            notifyRecipientDataStale(id);
            const res = await apiRequest<Recipient>(`/recipients/${id}`, {
                method: "GET",
                cache: "no-store",
            });
            const next = snapshotRecipient(res);
            setRecipient(next);
            setError(null);

            const prev = prevStatusRef.current;
            const cur = next.questionnaireStatus;
            if (prev === "sent" && cur === "submitted") {
                onSubmittedRef.current?.();
                setTimeout(() => void load(), 600);
                setTimeout(() => void load(), 2_000);
            }
            prevStatusRef.current = cur;

            return next;
        } catch (e) {
            setError(e instanceof ApiError ? e.message : "Failed to load data.");
            return null;
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        prevStatusRef.current = undefined;
        setRecipient(null);
        setLoading(!!id);
        setError(null);
        stopPoll();
        if (!id) return;
        void load();
        return stopPoll;
    }, [id, load, stopPoll]);

    // Fast while a questionnaire is awaiting submission, slow otherwise. Only while the tab is
    // visible — a hidden tab catches up on focus/visibilitychange below.
    useEffect(() => {
        if (!id) return;
        const status = recipient?.questionnaireStatus;
        const awaiting = status === "sent" || status === "opened";
        stopPoll();
        pollRef.current = setInterval(() => {
            if (document.visibilityState === "visible") void load();
        }, awaiting ? POLL_MS : IDLE_POLL_MS);
        return stopPoll;
    }, [id, recipient?.questionnaireStatus, load, stopPoll]);

    useEffect(() => {
        if (!id) return;
        const wake = () => {
            if (document.visibilityState === "visible") void load();
        };
        window.addEventListener("focus", wake);
        document.addEventListener("visibilitychange", wake);
        return () => {
            window.removeEventListener("focus", wake);
            document.removeEventListener("visibilitychange", wake);
        };
    }, [id, load]);

    const setOnSubmitted = useCallback((fn: (() => void) | null) => {
        onSubmittedRef.current = fn;
    }, []);

    return {
        recipient,
        loading,
        error,
        navigate,
        id,
        reload: load,
        setOnSubmitted,
    };
}
